// /app/frontend/src/components/frugal-v2/components/ZenChatModal.jsx
// Maestro ZEN Chat Modal for Frugal V2 (180 lines)

import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Loader2 } from 'lucide-react';
import * as api from '../services/frugalApi';

const ZenChatModal = ({ isOpen, onClose, token, t, language = 'es', financialContext = null }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const messagesEndRef = useRef(null);

  // Load chat history on open
  useEffect(() => {
    if (isOpen && token) {
      loadHistory();
    }
  }, [isOpen, token]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const data = await api.getChatHistory(token);
      setMessages(data.messages || []);
    } catch (err) {
      console.error('Error loading chat history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    
    // Add user message immediately
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await api.sendChatMessage(token, userMessage, language, financialContext);
      setMessages(prev => [...prev, { role: 'assistant', content: response.text }]);
    } catch (err) {
      console.error('Error sending message:', err);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: language === 'es' 
          ? 'Disculpa, hubo un error. Intenta de nuevo.' 
          : 'Sorry, there was an error. Please try again.'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      
      {/* Modal */}
      <div className="relative w-full sm:max-w-md h-[85vh] sm:h-[600px] bg-slate-900 sm:rounded-2xl overflow-hidden border border-emerald-500/20 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-gradient-to-r from-emerald-900/50 to-slate-900 flex-shrink-0">
          <div className="flex items-center gap-3">
            <img 
              src="/little-zen-frugal.png" 
              alt="Maestro ZEN" 
              className="w-10 h-10 rounded-full"
            />
            <div>
              <h3 className="text-white font-semibold">Maestro ZEN</h3>
              <p className="text-xs text-emerald-400">
                {language === 'es' ? 'Tu consejero financiero' : 'Your financial advisor'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loadingHistory ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8">
              <img 
                src="/little-zen-frugal.png" 
                alt="Maestro ZEN" 
                className="w-20 h-20 mx-auto mb-4 opacity-70"
              />
              <p className="text-slate-400 text-sm">
                {language === 'es' 
                  ? '¡Hola! Soy el Maestro ZEN. Pregúntame sobre tus finanzas.' 
                  : 'Hi! I am the ZEN Master. Ask me about your finances.'}
              </p>
            </div>
          ) : (
            messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'assistant' && (
                  <img 
                    src="/little-zen-frugal.png" 
                    alt="ZEN" 
                    className="w-8 h-8 rounded-full mr-2 flex-shrink-0"
                  />
                )}
                <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                  msg.role === 'user' 
                    ? 'bg-emerald-600 text-white rounded-br-md' 
                    : 'bg-slate-800 text-slate-200 rounded-bl-md'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))
          )}
          
          {loading && (
            <div className="flex justify-start">
              <img 
                src="/little-zen-frugal.png" 
                alt="ZEN" 
                className="w-8 h-8 rounded-full mr-2 flex-shrink-0"
              />
              <div className="bg-slate-800 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-slate-700 bg-slate-800/50 flex-shrink-0">
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={language === 'es' ? '¿Cómo puedo optimizar mis gastos?' : 'How can I optimize my spending?'}
              className="flex-1 bg-slate-700 border border-slate-600 rounded-xl px-4 py-2.5 text-white text-sm placeholder-slate-400 focus:outline-none focus:border-emerald-500"
              disabled={loading}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || loading}
              className="px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-colors"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 text-white animate-spin" />
              ) : (
                <Send className="w-5 h-5 text-white" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZenChatModal;
